import React, { useEffect, useRef, useState } from "react";
import {
  clampViewerScale,
  panViewer,
  pinchViewerScale,
  viewerOrientationChanged,
} from "../../feedback/feedbackImageViewer";

function touchDistance(first, second) {
  return Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY);
}

export function FeedbackImageViewer({ src, alt = "Feedback screenshot", onClose }) {
  const onCloseRef = useRef(onClose);
  const stageRef = useRef(null);
  const closeRef = useRef(null);
  const gestureRef = useRef(null);
  const viewRef = useRef({ scale: 1, x: 0, y: 0 });
  const [view, setView] = useState(viewRef.current);

  onCloseRef.current = onClose;
  viewRef.current = view;

  function applyView(next) {
    viewRef.current = next;
    setView(next);
  }

  function requestClose() {
    if (window.history.state?.feedbackAttachmentViewer) {
      window.history.back();
      return;
    }
    onCloseRef.current();
  }

  useEffect(() => {
    window.history.pushState({ feedbackAttachmentViewer: true }, "");
    closeRef.current?.focus();
    let popped = false;
    function onPop() {
      popped = true;
      onCloseRef.current();
    }
    function onKey(event) {
      if (event.key === "Escape") requestClose();
    }
    window.addEventListener("popstate", onPop);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("keydown", onKey);
      if (!popped && window.history.state?.feedbackAttachmentViewer) {
        window.history.back();
      }
    };
  }, []);

  useEffect(() => {
    let orientation = {
      landscape: window.innerWidth > window.innerHeight,
    };
    function onResize() {
      const next = { landscape: window.innerWidth > window.innerHeight };
      if (viewerOrientationChanged(orientation, next)) {
        applyView({ scale: 1, x: 0, y: 0 });
      }
      orientation = next;
    }
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    function onTouchMove(event) {
      const gesture = gestureRef.current;
      if (!gesture) return;
      if (gesture.type === "pinch" && event.touches.length === 2) {
        event.preventDefault();
        const scale = pinchViewerScale(
          gesture.startScale,
          gesture.startDistance,
          touchDistance(event.touches[0], event.touches[1]),
        );
        const next = scale <= 1
          ? { scale: 1, x: 0, y: 0 }
          : panViewer(scale, gesture.startX, gesture.startY, stage.clientWidth, stage.clientHeight);
        applyView(next);
      } else if (gesture.type === "pan" && event.touches.length === 1) {
        event.preventDefault();
        const touch = event.touches[0];
        applyView(panViewer(
          viewRef.current.scale,
          gesture.startX + (touch.clientX - gesture.originX),
          gesture.startY + (touch.clientY - gesture.originY),
          stage.clientWidth,
          stage.clientHeight,
        ));
      }
    }
    stage.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => stage.removeEventListener("touchmove", onTouchMove);
  }, []);

  function onTouchStart(event) {
    if (event.touches.length === 2) {
      gestureRef.current = {
        type: "pinch",
        startScale: viewRef.current.scale,
        startDistance: touchDistance(event.touches[0], event.touches[1]),
        startX: viewRef.current.x,
        startY: viewRef.current.y,
      };
      return;
    }
    if (event.touches.length === 1 && viewRef.current.scale > 1) {
      gestureRef.current = {
        type: "pan",
        startX: viewRef.current.x,
        startY: viewRef.current.y,
        originX: event.touches[0].clientX,
        originY: event.touches[0].clientY,
      };
    }
  }

  function onTouchEnd(event) {
    if (event.touches.length > 0) return;
    const now = Date.now();
    const previous = gestureRef.current?.lastTap || 0;
    if (gestureRef.current?.type !== "pinch" && now - previous < 300) {
      applyView(viewRef.current.scale > 1 ? { scale: 1, x: 0, y: 0 } : { scale: 2, x: 0, y: 0 });
      gestureRef.current = null;
      return;
    }
    gestureRef.current = { lastTap: now };
  }

  function onPointerDown(event) {
    if (event.pointerType === "touch" || viewRef.current.scale <= 1) return;
    gestureRef.current = {
      type: "pointer",
      startX: viewRef.current.x,
      startY: viewRef.current.y,
      originX: event.clientX,
      originY: event.clientY,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }

  function onPointerMove(event) {
    const gesture = gestureRef.current;
    const stage = stageRef.current;
    if (!gesture || gesture.type !== "pointer" || !stage) return;
    applyView(panViewer(
      viewRef.current.scale,
      gesture.startX + (event.clientX - gesture.originX),
      gesture.startY + (event.clientY - gesture.originY),
      stage.clientWidth,
      stage.clientHeight,
    ));
  }

  function onWheel(event) {
    event.preventDefault();
    const stage = stageRef.current;
    const factor = event.deltaY < 0 ? 1.12 : 1 / 1.12;
    const scale = clampViewerScale(viewRef.current.scale * factor);
    applyView(panViewer(
      scale,
      viewRef.current.x,
      viewRef.current.y,
      stage?.clientWidth || 0,
      stage?.clientHeight || 0,
    ));
  }

  function zoomBy(factor) {
    const stage = stageRef.current;
    const scale = clampViewerScale(viewRef.current.scale * factor);
    applyView(panViewer(
      scale,
      viewRef.current.x,
      viewRef.current.y,
      stage?.clientWidth || 0,
      stage?.clientHeight || 0,
    ));
  }

  return (
    <div className="fb-viewer" role="dialog" aria-modal="true" aria-label={alt}>
      <div className="fb-viewer__bar">
        <button type="button" onClick={() => zoomBy(1.25)} aria-label="Zoom in">+</button>
        <button type="button" onClick={() => zoomBy(0.8)} aria-label="Zoom out">−</button>
        <button type="button" ref={closeRef} onClick={requestClose}>Close</button>
      </div>
      <div
        className="fb-viewer__stage"
        ref={stageRef}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onWheel={onWheel}
      >
        <img
          className="fb-viewer__image"
          src={src}
          alt=""
          draggable={false}
          style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }}
        />
      </div>
    </div>
  );
}
